import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { APP_BRAND_CONFIG } from '@indutrust/shared-model';
import { EarthLoaderComponent } from '@indutrust/shared-ui';
import { provideTranslocoScopeSharedGlobal } from '@indutrust/shared-util';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'idt-global-loader',
  imports: [EarthLoaderComponent, NgOptimizedImage, TranslocoDirective],
  template: `
    @if (brandConfig.logoDarkUrl) {
      <img
        class="logo"
        height="32"
        priority
        width="230"
        [alt]="brandConfig.appName"
        [ngSrc]="brandConfig.logoDarkUrl" />
    } @else if (brandConfig.logoUrl) {
      <img
        class="logo"
        height="32"
        priority
        width="230"
        [alt]="brandConfig.appName"
        [ngSrc]="brandConfig.logoUrl" />
    } @else {
      <span class="text-primary-contrast text-3xl font-bold">{{
        brandConfig.appName
      }}</span>
    }

    <p
      *transloco="let t; prefix: 'global'"
      class="text-primary-contrast text-2xl font-semibold">
      {{ t('app.init.inProgress') }}
    </p>

    <idt-earth-loader class="mt-4" />
  `,
  styles: `
    @use 'styles/platform/platform-config-variables' as *;

    :host {
      display: flex;
      justify-content: center;
      flex-direction: column;
      text-align: center;
      align-items: center;
      gap: 1rem;
      height: 100dvh;
      overflow: hidden;
      padding: 1rem;
      width: 100%;
      background: var(--primary-color-gradient);
      /* Splash is a branded light surface. Pin the tokens it (and
         idt-earth-loader) read so .app-dark on <html> cannot retheme it. */
      color-scheme: light;
      --p-content-background: #ffffff;
      --p-primary-contrast-color: #ffffff;
    }

    @media (max-width: ($breakpoint-sm - 1px)) {
      .content-container {
        padding: 0.25rem;
      }
    }
  `,
  providers: [provideTranslocoScopeSharedGlobal()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Full-screen global loader shown while the application initializes.
 *
 * Reads the brand logo and name from {@link APP_BRAND_CONFIG}.
 * Displays the logo when `logoUrl` is provided, otherwise falls back to the
 * `appName` text. Also shows a localized loading message and an animated earth loader.
 *
 * Always uses the light brand treatment (primary gradient, contrast text, white
 * earth-loader ring), even when `.app-dark` is on `<html>`.
 *
 * @example
 * <idt-global-loader></idt-global-loader>
 */
export class GlobalLoaderComponent {
  protected readonly brandConfig = inject(APP_BRAND_CONFIG);
}
